/**
 * Inițializare MapLibre GL JS + layer raster CAMS.
 * Masca România e aplicată la nivel de canvas (render.js), nu MapLibre.
 */
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { sliceToCanvas, computeBounds, setMaskPolygons, setMaskActive } from './render.js';

let map = null;
let currentBounds = null;
let markerEl = null;
let marker = null;
let maskEnabled = localStorage.getItem('cams-mask-ro') !== 'false'; // default ON

// Dark basemap (CartoDB Dark Matter — gratuit, fără API key)
const STYLE_URL = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

/**
 * Inițializează harta MapLibre, centrată pe România.
 */
export function initMap() {
  map = new maplibregl.Map({
    container: 'map',
    style: STYLE_URL,
    center: [24.8, 45.85],
    zoom: 6.4,
    minZoom: 4,
    maxZoom: 10,
    attributionControl: true,
    preserveDrawingBuffer: true,
  });

  // Controale
  map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
  map.addControl(new maplibregl.ScaleControl({ maxWidth: 120 }), 'bottom-right');

  // Geolocație (util pe mobil)
  map.addControl(
    new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: false,
    }),
    'top-right'
  );

  return new Promise(resolve => {
    map.on('load', () => resolve(map));
  });
}

/**
 * Adaugă sau actualizează layer-ul raster CAMS pe hartă.
 * @param {object} cube - Cubul de date
 * @param {number} timeIndex - Indexul timestep-ului curent
 * @param {string} variable - Variabila curentă
 * @param {Set<number>} [selectedIds] - ID-urile celulelor selectate (crossfilter)
 * @param {boolean} [showPopulation] - Dacă este true, afișează populația în loc de poluant
 */
export function updateRasterLayer(cube, timeIndex, variable, selectedIds = null, showPopulation = false) {
  const dataUrl = sliceToCanvas(cube, timeIndex, variable, selectedIds, showPopulation);
  const bounds = computeBounds(cube.lats, cube.lons);

  if (map.getSource('cams')) {
    // Actualizare imagine existentă
    map.getSource('cams').updateImage({
      url: dataUrl,
      coordinates: bounds,
    });
  } else {
    // Prima adăugare
    map.addSource('cams', {
      type: 'image',
      url: dataUrl,
      coordinates: bounds,
    });

    map.addLayer(
      {
        id: 'cams-layer',
        type: 'raster',
        source: 'cams',
        paint: {
          'raster-opacity': 0.75,
          'raster-resampling': 'linear',
          'raster-fade-duration': 0,
        },
      },
      // Inserăm sub label-urile de pe hartă
      getFirstLabelLayer()
    );
  }

  currentBounds = bounds;
}

// ── Romania Mask (canvas-level) ──────────────────────────────────

/**
 * Încarcă GeoJSON-ul și extrage ring-urile pentru clipping pe canvas.
 * Apelat o singură dată la boot. Poligoanele sunt trimise la render.js.
 */
export async function loadMaskBoundary() {
  try {
    const resp = await fetch('./data/romania_boundary.geojson');
    if (!resp.ok) {
      console.warn('⚠️ Nu s-a putut încărca romania_boundary.geojson');
      return;
    }
    const geojson = await resp.json();

    // Extragem ring-urile exterioare din toate poligoanele (județele)
    const rings = [];
    for (const feature of geojson.features) {
      const geom = feature.geometry;
      if (geom.type === 'Polygon') {
        rings.push(geom.coordinates[0]);
      } else if (geom.type === 'MultiPolygon') {
        for (const polygon of geom.coordinates) {
          rings.push(polygon[0]);
        }
      }
    }

    // Trimitem ring-urile la render.js
    setMaskPolygons(rings);
    setMaskActive(maskEnabled);
    console.log(`✓ Mască România: ${rings.length} poligoane încărcate (${maskEnabled ? 'activă' : 'inactivă'})`);

  } catch (error) {
    console.warn('⚠️ Eroare la încărcarea graniței România:', error);
  }
}

/**
 * Activează/dezactivează masca România (canvas-level).
 * @param {boolean} enabled
 */
export function setMaskEnabled(enabled) {
  maskEnabled = enabled;
  localStorage.setItem('cams-mask-ro', enabled ? 'true' : 'false');
  setMaskActive(enabled);
}

/**
 * Returnează starea curentă a măștii.
 */
export function isMaskEnabled() {
  return maskEnabled;
}

/**
 * Înregistrează handler de click pe hartă.
 */
export function onMapClick(handler) {
  map.on('click', (e) => {
    handler(e.lngLat.lng, e.lngLat.lat);
  });
}

/**
 * Plasează sau mută marker-ul de selecție pe hartă.
 */
export function placeMarker(lng, lat) {
  if (!markerEl) {
    markerEl = document.createElement('div');
    markerEl.className = 'pixel-marker';
    markerEl.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" stroke-width="2.5">
        <circle cx="12" cy="12" r="6" fill="rgba(0,212,255,0.15)" />
        <line x1="12" y1="2" x2="12" y2="8" />
        <line x1="12" y1="16" x2="12" y2="22" />
        <line x1="2" y1="12" x2="8" y2="12" />
        <line x1="16" y1="12" x2="22" y2="12" />
      </svg>
    `;
    // Stil inline pentru marker (nu e în CSS)
    markerEl.style.cssText = 'cursor: pointer; filter: drop-shadow(0 0 6px rgba(0,212,255,0.5));';
  }

  if (marker) {
    marker.setLngLat([lng, lat]);
  } else {
    marker = new maplibregl.Marker({ element: markerEl, anchor: 'center' })
      .setLngLat([lng, lat])
      .addTo(map);
  }
}

/**
 * Returnează obiectul map (pentru acces extern dacă e nevoie).
 */
export function getMap() {
  return map;
}

// ── City highlight marker ────────────────────────────────────────

let cityMarkerEl = null;
let cityMarker = null;

/**
 * Plasează sau mută marker-ul de evidențiere a orașului selectat.
 * @param {number} lng - Longitudine
 * @param {number} lat - Latitudine
 * @param {string} name - Numele orașului
 * @param {string} color - Culoarea accentului (hex/css)
 */
export function placeCityMarker(lng, lat, name, color = '#00d4ff') {
  if (!cityMarkerEl) {
    cityMarkerEl = document.createElement('div');
    cityMarkerEl.className = 'city-map-marker';
    cityMarkerEl.innerHTML = `
      <div class="city-marker-pulse" style="--marker-color: ${color}"></div>
      <div class="city-marker-dot" style="--marker-color: ${color}"></div>
      <div class="city-marker-label">${name}</div>
    `;
  } else {
    cityMarkerEl.querySelector('.city-marker-pulse').style.setProperty('--marker-color', color);
    cityMarkerEl.querySelector('.city-marker-dot').style.setProperty('--marker-color', color);
    cityMarkerEl.querySelector('.city-marker-label').textContent = name;
  }

  if (cityMarker) {
    cityMarker.setLngLat([lng, lat]);
  } else {
    cityMarker = new maplibregl.Marker({ element: cityMarkerEl, anchor: 'center' })
      .setLngLat([lng, lat])
      .addTo(map);
  }
}

/**
 * Elimină marker-ul de oraș de pe hartă.
 */
export function removeCityMarker() {
  if (cityMarker) {
    cityMarker.remove();
    cityMarker = null;
    cityMarkerEl = null;
  }
}

// ── Helpers ──────────────────────────────────────────────────────


function getFirstLabelLayer() {
  const layers = map.getStyle().layers;
  for (const layer of layers) {
    if (layer.type === 'symbol' && layer.layout?.['text-field']) {
      return layer.id;
    }
  }
  return undefined;
}

